// "You have purchased N Mao coins!" — the moment after a successful Stripe payment.
//
// The game dims behind a parchment card (a gentle rain of gold coins, a thin countdown line). The player taps
// "Seen and Noted" — or does nothing and it fades out after 5 s. showPurchaseCelebration() resolves the instant it starts
// to close, and the caller then adds the coins, so the treasury visibly counts up as the card fades away.
//
// Waits for the loading splash first (right after returning from Stripe the page is still loading, and a card shown behind
// the splash would burn its 5 s unseen). The coins themselves are never touched here.

import { playChime } from '../game/audio.js'

const AUTO_MS = 5000
const autoMs = () => (import.meta.env?.DEV && Number(window.__celebrationMs)) || AUTO_MS // (a test can hold the card open longer)
const FADE_MS = 650
const COIN_COUNT = 16

let active = null

function whenSplashGone(maxMs = 30000) {
  return new Promise((resolve) => {
    const t0 = Date.now()
    const check = () => {
      if (!document.getElementById('splash') || Date.now() - t0 > maxMs) return resolve()
      setTimeout(check, 150)
    }
    check()
  })
}

const reduceMotion = () => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches

// coins: how many Mao coins were bought (whole number). Resolves when the card starts to close.
export function showPurchaseCelebration({ coins }) {
  if (active) return active
  active = (async () => {
    await whenSplashGone()
    return new Promise((resolve) => {
      const amount = Math.max(0, Math.floor(Number(coins) || 0)).toLocaleString('en-US')
      const el = document.createElement('div')
      el.className = 'purchase-celebration'
      el.setAttribute('role', 'dialog')
      el.setAttribute('aria-modal', 'true')
      el.setAttribute('aria-labelledby', 'pc-title')
      el.style.setProperty('--pc-auto', `${autoMs()}ms`)
      el.innerHTML = `
        <div class="pc__rain" aria-hidden="true"></div>
        <div class="pc__card">
          <div class="pc__seal" aria-hidden="true">猫</div>
          <h2 class="pc__title" id="pc-title">You have purchased <span class="pc__amount">${amount}</span> Mao coins!</h2>
          <p class="pc__msg">You are rising in status, climbing the ladder of kingdom success, and catching the attention of the emperor. Congratulations!</p>
          <button class="pc__btn" type="button" data-done>Seen and Noted</button>
          <div class="pc__timer" aria-hidden="true"><i></i></div>
        </div>`

      if (!reduceMotion()) {
        const rain = el.querySelector('.pc__rain')
        for (let i = 0; i < COIN_COUNT; i++) {
          const c = document.createElement('i')
          c.style.left = `${4 + Math.random() * 92}%`
          c.style.animationDelay = `${(Math.random() * 1.6).toFixed(2)}s`
          c.style.animationDuration = `${(2.4 + Math.random() * 1.8).toFixed(2)}s`
          c.style.setProperty('--sz', `${14 + Math.round(Math.random() * 12)}px`)
          c.style.setProperty('--sway', `${Math.round(Math.random() * 60 - 30)}px`)
          rain.appendChild(c)
        }
      }

      document.body.appendChild(el)
      const btn = el.querySelector('[data-done]')
      let finished = false
      let timer = null
      const finish = (by = 'timer') => {
        if (finished) return
        finished = true
        clearTimeout(timer)
        if (import.meta.env?.DEV) window.__celebration = { ...window.__celebration, closedAt: Date.now(), by } // dev/test: real on-screen time
        document.removeEventListener('keydown', onKey, true)
        active = null
        el.classList.remove('on')
        el.classList.add('leaving')
        setTimeout(() => el.remove(), FADE_MS + 50)
        resolve() // the caller adds the coins now, while the card is still fading
      }
      const onKey = (e) => { if (e.key === 'Escape' || e.key === 'Enter') { e.preventDefault(); finish('key') } }
      document.addEventListener('keydown', onKey, true)
      btn.addEventListener('click', () => finish('button'))

      void el.offsetWidth // commit the hidden starting style, so the fade-in animates (no dependence on animation frames, which a background tab doesn't run)
      el.classList.add('on')
      try { btn.focus({ preventScroll: true }) } catch { /* ignore */ }
      playChime()
      if (import.meta.env?.DEV) window.__celebration = { shownAt: Date.now() }
      timer = setTimeout(() => finish('timer'), autoMs())
    })
  })().finally(() => { active = null })
  return active
}
